import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users, TrendingUp, Mail, Phone,
  Eye, Shield, Activity, CheckCircle, Megaphone,
  Search, Filter, Send, XCircle, Clock3,
  SlidersHorizontal
} from 'lucide-react';
import axios from 'axios';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import CreateUserModal from './CreateUserModal';
import { UserDetailModal } from './UserDetailModal';
import type {
  AdminUser,
  AdminMetrics,
} from '../../types';
import {
  adminWhatsAppStatusClass,
  adminWhatsAppStatusLabel,
} from '../../lib/metaAccountHealth';

type AdminTab = 'overview' | 'users' | 'analytics';
type AnalyticsGroupBy = 'day' | 'week' | 'month';
type AnalyticsCampaignType = '' | 'marketing' | 'renewal' | 'dynamic' | 'calendar';

type CurrencyAmount = {
  currency: string;
  amount: number;
};

interface AnalyticsData {
  filters: {
    start_date: string;
    end_date: string;
    user_id: string | null;
    campaign_type: string | null;
    group_by: AnalyticsGroupBy;
    timezone: string;
  };

  summary: {
    sent: number;
    delivered: number;
    failed: number;
    pending: number;
    delivery_rate: number;

    users_created: number;
    free_trial_started: number;
    paywall_signups?: number;
    paywall_paid?: number;
    active_accounts: number;
    new_paid_accounts: number;

    churn: number;
    voluntary_churn: number;
    involuntary_churn: number;
    net_paid_growth: number;

    mrr: CurrencyAmount[];
    mrr_accounts: number;
    revenue_collected: CurrencyAmount[];
  };

  free_trial_history: Array<{
    period: string;
    started: number;
  }>;

  active_accounts_history: Array<{
    period: string;
    active: number;
  }>;

  paid_accounts_history: Array<{
    period: string;
    new_paid: number;
  }>;

  churn_history: Array<{
    period: string;
    voluntary: number;
    involuntary: number;
    total: number;
  }>;

  paid_vs_churn_history: Array<{
    period: string;
    new_paid: number;
    churn: number;
    net_growth: number;
  }>;

  active_accounts_by_plan: Array<{
    plan: string;
    plan_display: string;
    count: number;
  }>;

  mrr_snapshot: {
    totals: CurrencyAmount[];
    accounts: number;
  };

  revenue: {
    totals: CurrencyAmount[];
    recurring: CurrencyAmount[];
    direct: CurrencyAmount[];
  };

  revenue_history: Array<{
    period: string;
    currency: string;
    recurring: number;
    direct: number;
    total: number;
  }>;

  message_history: Array<{
    period: string;
    sent: number;
    delivered: number;
    failed: number;
    pending: number;
  }>;

  user_history: Array<{
    period: string;
    created: number;
  }>;

  new_accounts: Array<{
    user_id: string;
    name: string;
    email: string | null;
    role: string | null;
    plan: string | null;
    created_at: string | null;
  }>;

  campaign_breakdown: Array<{
    type: string;
    count: number;
  }>;

  account_activity: Array<{
    user_id: string;
    name: string;
    sent: number;
    delivered: number;
    failed: number;
    pending: number;
  }>;
}

// Rango automático: del primer día del mes actual hasta hoy (hora local del navegador).
const getCurrentMonthRange = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return {
    startDate: `${year}-${month}-01`,
    endDate: `${year}-${month}-${day}`,
  };
};

const USERS_PAGE_SIZE = 20;

interface AdminUsersPage {
  users: AdminUser[];
  total: number;
  page: number;
  page_size: number;
}

interface AccountOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);

  const [analyticsStartDate, setAnalyticsStartDate] = useState(() => getCurrentMonthRange().startDate);
  const [analyticsEndDate, setAnalyticsEndDate] = useState(() => getCurrentMonthRange().endDate);
  const [analyticsUserId, setAnalyticsUserId] = useState('');
  const [analyticsCampaignType, setAnalyticsCampaignType] = useState<AnalyticsCampaignType>('');
  const [analyticsGroupBy, setAnalyticsGroupBy] = useState<AnalyticsGroupBy>('day');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersRefresh, setUsersRefresh] = useState(0);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const usersRequest = useRef(0);

  // Search and Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [sortBy, setSortBy] = useState<'created_at' | 'last_login' | 'first_name'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const loadMetrics = async (): Promise<void> => {
      setLoading(true);
      try {
        const metricsRes = await axios.get<AdminMetrics>('/admin/metrics');
        setMetrics(metricsRes.data);
      } catch (error) {
        console.error('Error loading admin data:', error);
        toast.error('Error al cargar datos del admin');
      } finally {
        setLoading(false);
      }
    };
    loadMetrics();
  }, []);

  useEffect(() => {
    const delay = searchTerm.trim() ? 300 : 0;
    const timeoutId = window.setTimeout(() => {
      const requestId = usersRequest.current + 1;
      usersRequest.current = requestId;
      setUsersLoading(true);
      axios.get<AdminUsersPage>('/admin/users', {
        params: {
          page: userPage,
          page_size: USERS_PAGE_SIZE,
          search: searchTerm.trim() || undefined,
          plan: planFilter || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        },
      }).then((usersRes) => {
        if (requestId !== usersRequest.current) return;
        setUsers(usersRes.data.users);
        setUserTotal(usersRes.data.total);
      }).catch((error) => {
        if (requestId !== usersRequest.current) return;
        console.error('Error loading admin users:', error);
        toast.error('Error al cargar las cuentas');
      }).finally(() => {
        if (requestId === usersRequest.current) setUsersLoading(false);
      });
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [userPage, searchTerm, planFilter, sortBy, sortOrder, usersRefresh]);

  useEffect(() => {
    if (activeTab !== 'analytics' || accountOptions.length > 0) return;
    axios.get<AccountOption[]>('/admin/users/directory')
      .then((response) => setAccountOptions(response.data))
      .catch((error) => console.error('Error loading account directory:', error));
  }, [activeTab, accountOptions.length]);

  // Zona horaria local detectada automáticamente desde el navegador
  const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const loadAnalytics = async (
    startDate: string = analyticsStartDate,
    endDate: string = analyticsEndDate
  ): Promise<void> => {
    if (!startDate || !endDate) {
      toast.error('Selecciona la fecha inicial y la fecha final');
      return;
    }

    if (startDate > endDate) {
      toast.error('La fecha inicial no puede ser posterior a la fecha final');
      return;
    }

    setAnalyticsLoading(true);
    setAnalyticsData(null);

    try {
      const params: Record<string, string> = {
        start_date: startDate,
        end_date: endDate,
        group_by: analyticsGroupBy,
        timezone_name: USER_TIMEZONE,
        _ts: Date.now().toString(),
      };

      if (analyticsUserId) {
        params.user_id = analyticsUserId;
      }

      if (analyticsCampaignType) {
        params.campaign_type = analyticsCampaignType;
      }

      const response = await axios.get<AnalyticsData>(
        '/admin/analytics',
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        }
      );

      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Error al cargar la analítica');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: USER_TIMEZONE,
    });
  };

  const formatCurrencyAmounts = (values: CurrencyAmount[]): string => {
    if (!values || values.length === 0) {
      return '$0';
    }

    return values
      .map(({ currency, amount }) =>
        new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(amount)
      )
      .join(' · ');
  };

  const totalUserPages = Math.max(1, Math.ceil(userTotal / USERS_PAGE_SIZE));

  const handleUserCreated = (_newUser: AdminUser): void => {
    setUserPage(1);
    setUsersRefresh((current) => current + 1);
    setAccountOptions([]);
    if (metrics) {
      setMetrics(prev => prev ? {
        ...prev,
        total_users: prev.total_users + 1
      } : null);
    }
  };

  const getRoleBadge = (role: string): React.ReactElement => {
    const roleConfig: Record<string, { color: string; icon: React.ComponentType<{ className?: string }>; label: string }> = {
      admin: { color: 'bg-[#E8F7F7] text-[#0D7377]', icon: Shield, label: 'Admin' },
      operator: { color: 'bg-blue-50 text-blue-700', icon: Users, label: 'Operador' }
    };

    const config = roleConfig[role] || roleConfig.operator;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  interface MetricCardProps {
    title: string;
    value: string | number;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    change?: number;
  }

  /* Updated Metric Card to match RenewalCampaigns.tsx style EXACTLY */
  const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon: Icon, color }) => {
    // Determine styles based on color prop (mapping generic bg classes to specific styling pairs)
    let textClass = "text-gray-900";
    let bgClass = "bg-gray-100";
    let iconColorClass = "text-gray-600";

    if (color.includes("blue")) {
      textClass = "text-blue-600";
      bgClass = "bg-blue-50";
      iconColorClass = "text-blue-600";
    } else if (color.includes("green")) {
      textClass = "text-green-600";
      bgClass = "bg-green-50";
      iconColorClass = "text-green-600";
    } else if (color.includes("purple")) {
      textClass = "text-purple-600";
      bgClass = "bg-purple-50";
      iconColorClass = "text-purple-600";
    } else if (color.includes("red")) {
      textClass = "text-red-600";
      bgClass = "bg-red-50";
      iconColorClass = "text-red-600";
    } else if (color.includes("amber")) {
      textClass = "text-amber-600";
      bgClass = "bg-amber-50";
      iconColorClass = "text-amber-600";
    } else if (color.includes("teal")) {
      textClass = "text-teal-600";
      bgClass = "bg-teal-50";
      iconColorClass = "text-teal-600";
    }

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-0">
        <div className="p-6 pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className={`text-2xl font-bold ${textClass}`}>{value}</p>
            </div>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bgClass}`}>
              <Icon className={`h-4 w-4 ${iconColorClass}`} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="animate-fadeIn">
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">Cargando panel de administración...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] rounded-xl shadow-lg p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">Panel de Administración</h1>
            <p className="text-blue-50">
              Bienvenido, {(user as AdminUser)?.first_name || user?.name} {(user as AdminUser)?.last_name || ''}
            </p>
          </div>
          <div className="flex items-center space-x-2 self-start sm:self-auto">
            <Shield className="h-8 w-8" />
            <span className="text-lg font-semibold">Admin</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex w-max min-w-full space-x-4 sm:space-x-8 px-4 sm:px-6">
            {[
              { id: 'overview' as AdminTab, label: 'Resumen', icon: Activity },
              { id: 'users' as AdminTab, label: 'Cuentas', icon: Users },
              { id: 'analytics' as AdminTab, label: 'Analítica', icon: TrendingUp  }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'analytics' && activeTab !== 'analytics') {
                      const { startDate, endDate } = getCurrentMonthRange();
                      setAnalyticsStartDate(startDate);
                      setAnalyticsEndDate(endDate);
                      void loadAnalytics(startDate, endDate);
                    }
                    setActiveTab(tab.id);
                  }}
                  className={`flex shrink-0 items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Metrics */}
              {/* Metrics - Grid of 3 to match design request "distribuye" */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <MetricCard
                  title="Mensajes Entregados (Facturados)"
                  value={metrics?.total_messages_delivered?.toLocaleString() || '0'}
                  icon={Megaphone}
                  color="bg-purple-500"
                />
                <MetricCard
                  title="Tasa Entrega (Histórico)"
                  value={
                    metrics?.total_messages_sent
                      ? `${Math.round((metrics.total_messages_delivered / metrics.total_messages_sent) * 100)}%`
                      : metrics?.total_messages_delivered
                        ? '100%'
                        : '0%'
                  }
                  icon={CheckCircle}
                  color="bg-green-500"
                />
                <MetricCard
                  title="Usuarios Totales"
                  value={metrics?.total_users || 0}
                  icon={Users}
                  color="bg-blue-500"
                />
              </div>
            </div>
          )}

                    {activeTab === 'analytics' && (
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Analítica
                          </h3>

                          <p className="text-sm text-gray-500 mt-1">
                            Consulta y analiza mensajes, campañas y crecimiento de cuentas en períodos personalizados.
                          </p>
                        </div>

                        {/* Filtros */}
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Desde
                              </label>
                              <input
                                type="date"
                                value={analyticsStartDate}
                                onChange={(e) => setAnalyticsStartDate(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Hasta
                              </label>
                              <input
                                type="date"
                                value={analyticsEndDate}
                                onChange={(e) => setAnalyticsEndDate(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cuenta
                              </label>
                              <select
                                value={analyticsUserId}
                                onChange={(e) => setAnalyticsUserId(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="">Todas las cuentas</option>

                                {accountOptions.map((userData) => (
                                  <option key={userData.id} value={userData.id}>
                                    {userData.first_name} {userData.last_name} — {userData.email}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tipo de campaña
                              </label>
                              <select
                                value={analyticsCampaignType}
                                onChange={(e) =>
                                  setAnalyticsCampaignType(
                                    e.target.value as AnalyticsCampaignType
                                  )
                                }
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="">Todas</option>
                                <option value="marketing">Marketing</option>
                                <option value="renewal">Recordatorios periódicos</option>
                                <option value="dynamic">Dinámicas</option>
                                <option value="calendar">Google Calendar</option>
                              </select>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-4">

                            <details>
                              <summary className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                                <SlidersHorizontal className="h-4 w-4" />
                                Opciones de visualización
                              </summary>

                              <div className="mt-3 w-full sm:w-64">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Agrupar por
                                </label>

                                <select
                                  value={analyticsGroupBy}
                                  onChange={(e) =>
                                    setAnalyticsGroupBy(
                                      e.target.value as AnalyticsGroupBy
                                    )
                                  }
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="day">Día</option>
                                  <option value="week">Semana</option>
                                  <option value="month">Mes</option>
                                </select>

                                <p className="text-xs text-gray-500 mt-1">
                                  Cambia únicamente la escala temporal de las gráficas.
                                </p>
                              </div>
                            </details>

                            <button
                              type="button"
                              onClick={() => void loadAnalytics()}
                              disabled={analyticsLoading}
                              className="px-5 py-2 bg-[#1F4068] text-white text-sm font-medium rounded-lg hover:bg-[#17334f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {analyticsLoading ? 'Consultando...' : 'Consultar'}
                            </button>
                          </div>
                        </div>

                        {/* Indicadores del período consultado */}
                        {analyticsData && (
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-base font-semibold text-gray-900">
                                Resumen del período
                              </h4>
                              <p className="text-sm text-gray-500 mt-1">
                                Indicadores calculados según los filtros seleccionados.
                              </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                              <MetricCard
                                title="Nuevas cuentas / Free Trial"
                                value={analyticsData.summary.free_trial_started.toLocaleString('es-CO')}
                                icon={Users}
                                color="bg-blue-500"
                              />

                              <MetricCard
                                title="Registros por campaña (pagaron / registrados)"
                                value={`${(analyticsData.summary.paywall_paid ?? 0).toLocaleString('es-CO')} / ${(analyticsData.summary.paywall_signups ?? 0).toLocaleString('es-CO')}`}
                                icon={Megaphone}
                                color="bg-amber-500"
                              />

                              <MetricCard
                                title="Cuentas activas"
                                value={analyticsData.summary.active_accounts.toLocaleString('es-CO')}
                                icon={Activity}
                                color="bg-teal-500"
                              />

                              <MetricCard
                                title="Nuevas cuentas de pago"
                                value={analyticsData.summary.new_paid_accounts.toLocaleString('es-CO')}
                                icon={CheckCircle}
                                color="bg-green-500"
                              />

                              <MetricCard
                                title="MRR"
                                value={formatCurrencyAmounts(analyticsData.summary.mrr)}
                                icon={TrendingUp}
                                color="bg-purple-500"
                              />

                              <MetricCard
                                title="Churn"
                                value={analyticsData.summary.churn.toLocaleString('es-CO')}
                                icon={XCircle}
                                color="bg-red-500"
                              />

                              <MetricCard
                                title="Crecimiento neto"
                                value={analyticsData.summary.net_paid_growth.toLocaleString('es-CO')}
                                icon={TrendingUp}
                                color={
                                  analyticsData.summary.net_paid_growth >= 0
                                    ? 'bg-green-500'
                                    : 'bg-red-500'
                                }
                              />
                            </div>
                            <div className="pt-2">
                              <h4 className="text-base font-semibold text-gray-900">
                                Indicadores de negocio
                              </h4>

                              <p className="text-sm text-gray-500 mt-1">
                                Ingresos, recurrencia y pérdida de clientes durante el período seleccionado.
                              </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                              <MetricCard
                                title="Ingresos cobrados"
                                value={formatCurrencyAmounts(analyticsData.summary.revenue_collected)}
                                icon={TrendingUp}
                                color="bg-green-500"
                              />

                              <MetricCard
                                title="Cuentas que generan MRR"
                                value={analyticsData.summary.mrr_accounts.toLocaleString('es-CO')}
                                icon={Users}
                                color="bg-purple-500"
                              />

                              <MetricCard
                                title="Churn voluntario"
                                value={analyticsData.summary.voluntary_churn.toLocaleString('es-CO')}
                                icon={XCircle}
                                color="bg-red-500"
                              />

                              <MetricCard
                                title="Churn involuntario"
                                value={analyticsData.summary.involuntary_churn.toLocaleString('es-CO')}
                                icon={Clock3}
                                color="bg-amber-500"
                              />
                            </div>
                            <div className="pt-2">
                              <h4 className="text-base font-semibold text-gray-900">
                                Analítica de producto
                              </h4>

                              <p className="text-sm text-gray-500 mt-1">
                                Rendimiento de mensajes y uso de campañas durante el período seleccionado.
                              </p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                              <MetricCard
                                title="Mensajes enviados (API)"
                                value={analyticsData.summary.sent.toLocaleString('es-CO')}
                                icon={Send}
                                color="bg-blue-500"
                              />

                              <MetricCard
                                title="Mensajes entregados"
                                value={analyticsData.summary.delivered.toLocaleString('es-CO')}
                                icon={CheckCircle}
                                color="bg-green-500"
                              />

                              <MetricCard
                                title="Mensajes fallidos"
                                value={analyticsData.summary.failed.toLocaleString('es-CO')}
                                icon={XCircle}
                                color="bg-red-500"
                              />

                              <MetricCard
                                title="Mensajes pendientes"
                                value={analyticsData.summary.pending.toLocaleString('es-CO')}
                                icon={Clock3}
                                color="bg-amber-500"
                              />

                              <MetricCard
                                title="Tasa de entrega"
                                value={`${analyticsData.summary.delivery_rate.toLocaleString(
                                  'es-CO',
                                  { maximumFractionDigits: 2 }
                                )}%`}
                                icon={TrendingUp}
                                color="bg-purple-500"
                              />

                              <MetricCard
                                title="Campañas registradas"
                                value={analyticsData.campaign_breakdown
                                  .reduce((total, item) => total + item.count, 0)
                                  .toLocaleString('es-CO')}
                                icon={Megaphone}
                                color="bg-teal-500"
                              />
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <div className="mb-5">
                                <h4 className="text-base font-semibold text-gray-900">
                                  Nuevas cuentas por período
                                </h4>
                                <p className="text-sm text-gray-500 mt-1">
                                  Nuevas cuentas creadas durante el período seleccionado.
                                </p>
                              </div>

                              {analyticsData.free_trial_history.length > 0 ? (
                                <div className="w-full h-80">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={analyticsData.free_trial_history}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                      <XAxis
                                        dataKey="period"
                                        tickLine={false}
                                      />

                                      <YAxis
                                        allowDecimals={false}
                                        tickLine={false}
                                      />

                                      <Tooltip />

                                      <Bar
                                        dataKey="started"
                                        name="Nuevas cuentas"
                                        fill="#2563EB"
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={70}
                                        label={{
                                          position: 'top',
                                          fill: '#374151',
                                          fontSize: 13,
                                        }}
                                      />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              ) : (
                                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                  No hay nuevas cuentas en el período seleccionado.
                                </div>
                              )}
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <div className="mb-5">
                                <h4 className="text-base font-semibold text-gray-900">
                                  Cuentas activas por período
                                </h4>

                                <p className="text-sm text-gray-500 mt-1">
                                  Cuentas que enviaron al menos un mensaje mediante una campaña.
                                </p>
                              </div>

                              {analyticsData.active_accounts_history.length > 0 ? (
                                <div className="w-full h-80">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={analyticsData.active_accounts_history}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                      <XAxis
                                        dataKey="period"
                                        tickLine={false}
                                      />

                                      <YAxis
                                        allowDecimals={false}
                                        tickLine={false}
                                      />

                                      <Tooltip />

                                      <Line
                                        type="monotone"
                                        dataKey="active"
                                        name="Cuentas activas"
                                        stroke="#0D9488"
                                        strokeWidth={3}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              ) : (
                                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                  No hubo cuentas activas en el período seleccionado.
                                </div>
                              )}
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <div className="mb-5">
                                <h4 className="text-base font-semibold text-gray-900">
                                  Nuevas cuentas de pago vs Churn vs Crecimiento real
                                </h4>

                                <p className="text-sm text-gray-500 mt-1">
                                  Compara la adquisición de nuevas cuentas de pago, la pérdida de clientes y el crecimiento real en cada período.
                                </p>
                              </div>

                              {analyticsData.paid_vs_churn_history.length > 0 ? (
                                <div className="w-full h-80">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={analyticsData.paid_vs_churn_history}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                      <XAxis
                                        dataKey="period"
                                        tickLine={false}
                                      />

                                      <YAxis
                                        allowDecimals={false}
                                        tickLine={false}
                                        domain={['auto', 'auto']}
                                      />

                                      <Tooltip />
                                      <Legend />

                                      <Line
                                        type="monotone"
                                        dataKey="new_paid"
                                        name="Nuevas cuentas de pago"
                                        stroke="#16A34A"
                                        strokeWidth={3}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                      />

                                      <Line
                                        type="monotone"
                                        dataKey="churn"
                                        name="Churn"
                                        stroke="#DC2626"
                                        strokeWidth={3}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                      />

                                      <Line
                                        type="monotone"
                                        dataKey="net_growth"
                                        name="Crecimiento real"
                                        stroke="#2563EB"
                                        strokeWidth={3}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              ) : (
                                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                  No hubo nuevas cuentas de pago ni churn en el período seleccionado.
                                </div>
                              )}
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <div className="mb-5">
                                <h4 className="text-base font-semibold text-gray-900">
                                  Cuentas activas por plan
                                </h4>

                                <p className="text-sm text-gray-500 mt-1">
                                  Distribución de las cuentas activas según el plan al final del período seleccionado.
                                </p>
                              </div>

                              {analyticsData.active_accounts_by_plan.some((item) => item.count > 0) ? (
                                <div className="w-full h-80">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                      data={analyticsData.active_accounts_by_plan}
                                      layout="vertical"
                                      margin={{ left: 20, right: 30 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />

                                      <XAxis
                                        type="number"
                                        allowDecimals={false}
                                        tickLine={false}
                                      />

                                      <YAxis
                                        type="category"
                                        dataKey="plan_display"
                                        tickLine={false}
                                        width={170}
                                      />

                                      <Tooltip />

                                      <Bar
                                        dataKey="count"
                                        name="Cuentas activas"
                                        fill="#7C3AED"
                                        radius={[0, 6, 6, 0]}
                                        maxBarSize={45}
                                        label={{
                                          position: 'right',
                                          fill: '#374151',
                                          fontSize: 13,
                                        }}
                                      />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              ) : (
                                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                  No hubo cuentas activas por plan en el período seleccionado.
                                </div>
                              )}
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <div className="mb-5">
                                <h4 className="text-base font-semibold text-gray-900">
                                  Ingresos cobrados por período
                                </h4>

                                <p className="text-sm text-gray-500 mt-1">
                                  Ingresos efectivamente cobrados, separados entre pagos recurrentes y directos.
                                </p>
                              </div>

                              {analyticsData.revenue_history.length > 0 ? (
                                <div className="space-y-6">
                                  {Array.from(
                                    new Set(
                                      analyticsData.revenue_history.map((item) => item.currency)
                                    )
                                  ).map((currency) => (
                                    <div key={currency}>
                                      <p className="text-sm font-semibold text-gray-700 mb-3">
                                        {currency}
                                      </p>

                                      <div className="w-full h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                          <BarChart
                                            data={analyticsData.revenue_history.filter(
                                              (item) => item.currency === currency
                                            )}
                                          >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                            <XAxis
                                              dataKey="period"
                                              tickLine={false}
                                            />

                                            <YAxis tickLine={false} />

                                            <Tooltip />
                                            <Legend />

                                            <Bar
                                              dataKey="recurring"
                                              name="Recurrente"
                                              fill="#7C3AED"
                                              radius={[6, 6, 0, 0]}
                                              maxBarSize={55}
                                            />

                                            <Bar
                                              dataKey="direct"
                                              name="Pago directo"
                                              fill="#0D9488"
                                              radius={[6, 6, 0, 0]}
                                              maxBarSize={55}
                                            />
                                          </BarChart>
                                        </ResponsiveContainer>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                  No hubo ingresos cobrados en el período seleccionado.
                                </div>
                              )}
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <div className="mb-5">
                                <h4 className="text-base font-semibold text-gray-900">
                                  Mensajes por período
                                </h4>

                                <p className="text-sm text-gray-500 mt-1">
                                  Evolución de los mensajes enviados, entregados, fallidos y pendientes.
                                </p>
                              </div>

                              {analyticsData.message_history.length > 0 ? (
                                <div className="w-full h-80">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={analyticsData.message_history}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                      <XAxis
                                        dataKey="period"
                                        tickLine={false}
                                      />

                                      <YAxis
                                        allowDecimals={false}
                                        tickLine={false}
                                      />

                                      <Tooltip />
                                      <Legend />

                                      <Line
                                        type="monotone"
                                        dataKey="sent"
                                        name="Enviados"
                                        stroke="#2563EB"
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                      />

                                      <Line
                                        type="monotone"
                                        dataKey="delivered"
                                        name="Entregados"
                                        stroke="#16A34A"
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                      />

                                      <Line
                                        type="monotone"
                                        dataKey="failed"
                                        name="Fallidos"
                                        stroke="#DC2626"
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                      />

                                      <Line
                                        type="monotone"
                                        dataKey="pending"
                                        name="Pendientes"
                                        stroke="#D97706"
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
</div>
                              ) : (
                                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                  No hubo actividad de mensajes en el período seleccionado.
                                </div>
                              )}
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <div className="mb-5">
                                <h4 className="text-base font-semibold text-gray-900">
                                  Campañas por tipo
                                </h4>

                                <p className="text-sm text-gray-500 mt-1">
                                  Distribución de las campañas registradas según su tipo.
                                </p>
                              </div>

                              {analyticsData.campaign_breakdown.some((item) => item.count > 0) ? (
                                <div className="w-full h-80">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie
                                        data={analyticsData.campaign_breakdown.map((item) => ({
                                          ...item,
                                          label:
                                            item.type === 'marketing'
                                              ? 'Marketing'
                                              : item.type === 'renewal'
                                                ? 'Recordatorios periódicos'
                                                : item.type === 'dynamic'
                                                  ? 'Dinámicas'
                                                  : item.type === 'calendar'
                                                    ? 'Google Calendar'
                                                    : item.type,
                                        }))}
                                        dataKey="count"
                                        nameKey="label"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={110}
                                        paddingAngle={3}
                                      >
                                        {analyticsData.campaign_breakdown.map((item, index) => (
                                          <Cell
                                            key={`${item.type}-${index}`}
                                            fill={
                                              ['#2563EB', '#7C3AED', '#0D9488', '#D97706'][
                                                index % 4
                                              ]
                                            }
                                          />
                                        ))}
                                      </Pie>

                                      <Tooltip />
                                      <Legend />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </div>
                              ) : (
                                <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                  No hubo campañas registradas en el período seleccionado.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Usuarios Registrados</h3>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">
                    {usersLoading ? 'Cargando cuentas...' : `${userTotal} usuarios${searchTerm || planFilter ? ' encontrados' : ''}`}
                  </span>
                </div>
              </div>

              {/* Toolbar con Filtros y Buscador */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:flex-wrap gap-4 items-stretch sm:items-center">
                <div className="w-full min-w-0 sm:flex-1 sm:min-w-[280px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email o teléfono..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setUserPage(1); }}
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    className="w-full sm:w-auto min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                    value={planFilter}
                    onChange={(e) => { setPlanFilter(e.target.value); setUserPage(1); }}
                  >
                    <option value="">Todos los planes</option>
                    <option value="free_trial">Free Trial</option>
                    <option value="basic_200">Básico (200)</option>
                    <option value="standard_1000">Estándar (1000)</option>
                    <option value="premium_3000">Premium (3000)</option>
                    <option value="unlimited">Ilimitado</option>
                    <option value="blocked">Bloqueado</option>
                    <option value="prepaid">Prepago</option>
                  </select>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-sm text-gray-400 font-medium">Ordenar:</span>
                  <select
                    className="flex-1 sm:flex-none min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value as typeof sortBy); setUserPage(1); }}
                  >
                    <option value="created_at">Fecha Registro</option>
                    <option value="last_login">Último Acceso</option>
                    <option value="first_name">Nombre</option>
                  </select>
                  <button
                    onClick={() => { setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); setUserPage(1); }}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-white transition-colors bg-white shadow-sm"
                    title={sortOrder === 'asc' ? 'Ascendente' : 'Descendente'}
                  >
                    <TrendingUp className={`h-4 w-4 transform transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-[1200px] w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Rol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Estado WhatsApp
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-emerald-600 uppercase tracking-wide whitespace-nowrap">
                        Campañas Activas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Último Acceso
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Fecha Registro
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wide whitespace-nowrap">
                        Enviados (API)
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wide whitespace-nowrap">
                        Entregados
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-red-600 uppercase tracking-wide whitespace-nowrap">
                        Fallidos
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Pendientes
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-indigo-600 uppercase tracking-wide whitespace-nowrap">
                        Uso Plan (Ciclo)
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!usersLoading && users.length === 0 && (
                      <tr>
                        <td colSpan={13} className="px-6 py-8 text-center text-sm text-gray-500">
                          No hay cuentas para mostrar.
                        </td>
                      </tr>
                    )}
                    {users.map((userData) => (
                      <tr key={userData.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] flex items-center justify-center text-white font-medium shadow-sm">
                                {userData.first_name?.charAt(0)}{userData.last_name?.charAt(0)}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {userData.first_name} {userData.last_name}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Mail className="h-3 w-3 mr-1 opacity-70" />
                                {userData.email}
                              </div>
                              {userData.phone && (
                                <div className="text-sm text-gray-500 flex items-center">
                                  <Phone className="h-3 w-3 mr-1 opacity-70" />
                                  {userData.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getRoleBadge(userData.role)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {userData.plan === 'unlimited' ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                              Ilimitado
                            </span>
                          ) : userData.plan === 'blocked' ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                              Bloqueado
                            </span>
                          ) : userData.plan === 'basic_200' ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                              Básico (200)
                            </span>
                          ) : userData.plan === 'standard_1000' ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                              Estándar (1000)
                            </span>
                          ) : userData.plan === 'premium_3000' ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">
                              Premium (3000)
                            </span>
                          ) : userData.plan === 'prepaid' ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-800 border border-teal-200">
                              Prepago
                            </span>
                          ) : (userData.plan === 'free_trial' || !userData.plan) ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                              Free Trial
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                              {userData.plan}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${adminWhatsAppStatusClass(
                              userData.whatsapp_account_status
                            )}`}
                          >
                            {adminWhatsAppStatusLabel(userData.whatsapp_account_status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-emerald-600">
                          {userData.active_campaigns_count ?? 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {userData.last_login ? formatDate(userData.last_login) : 'Nunca'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {userData.created_at ? formatDate(userData.created_at) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-blue-600">
                          {userData.sent_messages ?? 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-green-600">
                          {userData.delivered_messages ?? 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-red-600">
                          {userData.failed_messages ?? 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-gray-900">
                          {userData.pending_messages ?? 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-indigo-600">
                          {userData.plan_cycle_usage ?? 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <button
                            onClick={() => { setSelectedUserId(userData.id); setShowDetailModal(true); }}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Ver Reporte Detallado"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* User Pagination Controls */}
              {userTotal > USERS_PAGE_SIZE && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4">
                  <span className="text-sm text-gray-500">Página {userPage} de {totalUserPages}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setUserPage(p => Math.max(1, p - 1))}
                      disabled={userPage === 1 || usersLoading}
                      className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setUserPage(p => Math.min(totalUserPages, p + 1))}
                      disabled={userPage === totalUserPages || usersLoading}
                      className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onUserCreated={handleUserCreated}
      />
      <UserDetailModal
        userId={selectedUserId}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onUserUpdated={() => setUsersRefresh((current) => current + 1)}
      />
    </div>
  );
};

export default AdminDashboard;